import { useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAtom } from "jotai";
import { authenticationAtom } from "../State";
import { useAdministrationAPI } from "../AdministrationAPI";
import { useModalContext } from "../components/ModalProvider";
import Editor from "@monaco-editor/react";
import mustache from "mustache";

import { ChevronsUpDown, Loader2, X } from "lucide-react";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
    Command,
    CommandGroup,
    CommandItem,
    CommandList,
} from "@/components/ui/command";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

const ROLE_OPTIONS: { key: string; text: string; roleId: string }[] = [
  { key: "customerAdministrator", text: "Administrator", roleId: "customerAdministrator" },
  { key: "technicalAdministrator", text: "Technik", roleId: "technicalAdministrator" },
  { key: "billingAdministrator", text: "Billing", roleId: "billingAdministrator" },
];

type MessageKind = "success" | "error";

export default function MailPanel() {
  const { t } = useTranslation();
  const [authentication] = useAtom(authenticationAtom) as any;
  const { sendAdministrativeEmail } = useAdministrationAPI();
  const { pushError, showKeywordConfirmDialog } = useModalContext() as any;
  const fieldId = useId();

  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [template, setTemplate] = useState("<p>Hallo {{user.firstName}} {{user.lastName}},</p>\n\n<p>...</p>");
  const [activeTab, setActiveTab] = useState<"edit" | "preview">("edit");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: MessageKind; text: string } | null>(null);
  const [lastResult, setLastResult] = useState<any>(null);
  // The role selection lastResult was produced with. The recipient set depends only on
  // roles and customers (customers is always "all"), so this is what tells us whether
  // lastResult.recipients still describes what a live send would hit.
  const [lastResultRoles, setLastResultRoles] = useState<string[] | null>(null);
  const [rolesOpen, setRolesOpen] = useState(false);

  const rolesLabelId = `${fieldId}-roles-label`;
  const customersId = `${fieldId}-customers`;
  const templateLabelId = `${fieldId}-template-label`;

  const userForPreview = {
    user: {
      firstName: authentication?.firstName ?? "",
      lastName: authentication?.lastName ?? "",
      email: authentication?.email ?? "",
    },
  };

  const renderedPreview = (() => {
    try {
      return mustache.render(template, userForPreview);
    } catch {
      return template;
    }
  })();

  const toggleRole = (key: string) => {
    setSelectedRoles((prev) =>
      prev.includes(key) ? prev.filter((r) => r !== key) : [...prev, key]
    );
  };

  const handleSend = async (test: boolean) => {
    setMessage(null);
    setLoading(true);
    try {
      const payload = {
        roles: selectedRoles.length === 0 ? [] : selectedRoles,
        customers: [] as string[],
        template,
        test,
      };
      const result = await sendAdministrativeEmail(payload);
      setLastResult(result);
      setLastResultRoles([...selectedRoles]);
      if (test) {
        setMessage({
          type: "success",
          text: t("administrationPage.mailPanel.testSent", {
            count: result.recipients?.length ?? 0,
          }),
        });
      } else {
        setMessage({
          type: "success",
          text: t("administrationPage.mailPanel.sent", {
            count: result.sent ?? 0,
            total: result.recipients?.length ?? 0,
          }),
        });
      }
    } catch (err: any) {
      pushError(err);
      setMessage({
        type: "error",
        text: err?.message || "Failed to send",
      });
    } finally {
      setLoading(false);
    }
  };

  const sameRoleSelection = (a: string[], b: string[]) =>
    a.length === b.length && [...a].sort().join("|") === [...b].sort().join("|");

  // An empty role selection means "no role filter", i.e. every user.
  const roleSummary =
    selectedRoles.length === 0
      ? "alle Rollen"
      : selectedRoles
          .map((role) => ROLE_OPTIONS.find((option) => option.roleId === role)?.text ?? role)
          .join(", ");

  // A test run returns the real live recipient list while only mailing the admin, so it
  // is the one way this panel can name the blast radius up front. Only trust the count
  // when the role selection has not changed since.
  const knownRecipientCount =
    lastResult && lastResultRoles && sameRoleSelection(lastResultRoles, selectedRoles)
      ? (lastResult.recipients?.length ?? null)
      : null;

  const confirmAndSend = () => {
    showKeywordConfirmDialog(
      "E-Mail wirklich senden?",
      <div className="flex flex-col gap-2">
        <div>
          Diese E-Mail wird sofort an echte Nutzer gesendet und kann nicht zurückgeholt werden.
        </div>
        <div>
          Empfänger:{" "}
          <span className="font-bold">
            {knownRecipientCount === null ? "Anzahl unbekannt" : `${knownRecipientCount} Nutzer`}
          </span>
        </div>
        <div>
          Rollen: <span className="font-bold">{roleSummary}</span>
        </div>
        <div>
          Kunden: <span className="font-bold">alle Kunden</span>
        </div>
        {knownRecipientCount === null && (
          <div>Führen Sie zuerst „Test“ aus, um die genaue Empfängerliste zu sehen.</div>
        )}
      </div>,
      "SENDEN",
      t("administrationPage.mailPanel.send"),
      () => handleSend(false)
    );
  };

  const rolesTriggerText =
    selectedRoles.length === 0
      ? t("administrationPage.mailPanel.rolesAll")
      : ROLE_OPTIONS.filter((option) => selectedRoles.includes(option.roleId))
          .map((option) => option.text)
          .join(", ");

  return (
    <div className="flex w-full min-w-0 max-w-[900px] flex-col gap-4">
      <h2 className="text-lg font-semibold">{t("administrationPage.mailPanel.title")}</h2>

      <div className="flex min-w-0 flex-wrap gap-6">
        <div className="flex min-w-[200px] flex-1 flex-col gap-1.5">
          <Label id={rolesLabelId}>{t("administrationPage.mailPanel.roles")}</Label>
          <Popover open={rolesOpen} onOpenChange={setRolesOpen}>
            <PopoverTrigger asChild>
              <Button
                type="button"
                variant="outline"
                role="combobox"
                aria-labelledby={rolesLabelId}
                aria-expanded={rolesOpen}
                className="w-full justify-between font-normal"
              >
                <span className={selectedRoles.length === 0 ? "truncate text-muted-foreground" : "truncate"}>
                  {rolesTriggerText}
                </span>
                <ChevronsUpDown aria-hidden className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
              <Command>
                <CommandList>
                  <CommandGroup>
                    {ROLE_OPTIONS.map((option) => (
                      <CommandItem
                        key={option.roleId}
                        value={option.text}
                        className="gap-2"
                        onSelect={() => toggleRole(option.roleId)}
                      >
                        <Checkbox
                          checked={selectedRoles.includes(option.roleId)}
                          tabIndex={-1}
                          className="pointer-events-none"
                        />
                        <span className="min-w-0 break-words">{option.text}</span>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
          <p className="text-sm text-muted-foreground">
            {t("administrationPage.mailPanel.rolesHint")}
          </p>
        </div>
        <div className="flex min-w-[200px] flex-1 flex-col gap-1.5">
          <Label htmlFor={customersId}>{t("administrationPage.mailPanel.customers")}</Label>
          <Select value="all" disabled>
            <SelectTrigger id={customersId}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("administrationPage.mailPanel.customersAll")}</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-sm text-muted-foreground">
            {t("administrationPage.mailPanel.customersHint")}
          </p>
        </div>
      </div>

      <div className="flex min-w-0 flex-col gap-2">
        {/* Überschrift statt `Label`: die Vorlage ist ein Bereich (Editor bzw.
            Vorschau), kein einzelnes Formularfeld. */}
        <h3 id={templateLabelId} className="text-sm font-medium leading-none">
          {t("administrationPage.mailPanel.template")}
        </h3>
        <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as "edit" | "preview")}>
          <TabsList className="max-w-full overflow-x-auto">
            <TabsTrigger value="edit">{t("administrationPage.mailPanel.edit")}</TabsTrigger>
            <TabsTrigger value="preview">{t("administrationPage.mailPanel.preview")}</TabsTrigger>
          </TabsList>
        </Tabs>
        {activeTab === "edit" ? (
          <div className="min-w-0 overflow-hidden rounded-md border">
            <Editor
              height="320px"
              defaultLanguage="html"
              value={template}
              onChange={(v) => setTemplate(v ?? "")}
              options={{
                minimap: { enabled: false },
                fontSize: 13,
                wordWrap: "on",
                scrollBeyondLastLine: false,
              }}
            />
          </div>
        ) : (
          <div
            className="min-h-[320px] min-w-0 overflow-x-auto rounded-md border bg-background p-4"
            role="region"
            aria-labelledby={templateLabelId}
          >
            <div dangerouslySetInnerHTML={{ __html: renderedPreview }} />
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Button className="max-sm:flex-1" onClick={confirmAndSend} disabled={loading}>
          {loading && <Loader2 aria-hidden className="mr-2 h-4 w-4 animate-spin" />}
          {loading
            ? t("administrationPage.mailPanel.sending")
            : t("administrationPage.mailPanel.send")}
        </Button>
        <Button
          variant="outline"
          className="max-sm:flex-1"
          onClick={() => handleSend(true)}
          disabled={loading}
        >
          {t("administrationPage.mailPanel.test")}
        </Button>
      </div>

      {message && (
        <Alert variant={message.type === "success" ? "success" : "destructive"}>
          <div className="flex min-w-0 items-center gap-2">
            <AlertDescription className="min-w-0 flex-1 break-words">
              {message.text}
            </AlertDescription>
            <Button
              variant="ghost"
              size="icon"
              className="-my-2 -mr-2 shrink-0"
              aria-label={t("global.close")}
              onClick={() => setMessage(null)}
            >
              <X aria-hidden className="h-4 w-4" />
            </Button>
          </div>
        </Alert>
      )}

      {lastResult && (
        <div className="flex min-w-0 flex-col gap-1">
          <h3 className="text-sm font-semibold">
            {lastResult.sentToSelf
              ? t("administrationPage.mailPanel.recipientsLiveRun", {
                  count: lastResult.recipients?.length ?? 0,
                })
              : t("administrationPage.mailPanel.recipients", {
                  count: lastResult.recipients?.length ?? 0,
                })}
          </h3>
          <div className="max-h-[240px] min-w-0 overflow-y-auto rounded-md bg-muted p-2 text-sm">
            {(lastResult.recipients ?? []).length === 0 ? (
              <div className="text-muted-foreground">
                {t("administrationPage.mailPanel.noRecipients")}
              </div>
            ) : (
              (lastResult.recipients ?? []).map((r: any, i: number) => (
                <div key={i} className="min-w-0 break-words">
                  {r.email}
                  {r.firstName || r.lastName ? ` (${[r.firstName, r.lastName].filter(Boolean).join(" ")})` : ""}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
